'use client';

import styles from './ClientChatContent.module.css';
import { getDateFromInputs } from '@/_utils/dates';
import type { ClientChatContentProps } from './ClientChatContent.types';
import type { Message } from '@/_types/models/conversations';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  InlineUnsafe,
  TrixEditor,
  UploadBox,
} from '@/_components';
import { ClientChatMessage } from './components/ClientChatMessage';
import { ClientChatThreadAside } from './components/ClientChatThreadAside';
import { MessageDialog } from './components/MessageDialog';
import { ClientConversationsContext } from '@/_contexts/client/ClientConversationsProvider';
import { DOCUMENT_ANSWER_PREFIX } from '@/_constants/documents';
import { QUESTION_SEPARATOR } from '@/_constants/questions';
import { UserContext } from '@/_contexts/UserProvider';
import { uploadToStorage } from '@/_api/client-documents';
import { useChatScroll } from '@/_hooks/useChatScroll';
import {
  useEffect,
  useCallback,
  useState,
  useContext,
  type FC,
  useMemo,
} from 'react';
import { validateFile } from '@/_utils/files';
import { ToastContext } from '@/_contexts/ToastProvider';
import { removeHtml } from '@/_utils/sanitize';
import { ScheduledMessageDialog } from './components/ScheduledMessageDialog';
import { MessageTagType } from '@/_types/messages';
import { getClasses } from '@/_utils/classnames';
import { CONVERSATION_MESSAGE_LIMIT } from '@/_constants/conversations';
import { UnreadThreadIndicator } from './components/UnreadThreadIndicator';

export const ClientChatContent: FC<ClientChatContentProps> = () => {
  const { user, selectedCompanyId, token, isUserReadOnly, isCompanyReadOnly } =
    useContext(UserContext);
  const {
    isMessagesLoading,
    isSending,
    sendMessage,
    selectedMessage: conversation,
    reloadMessages,
    setThreadSeen,
  } = useContext(ClientConversationsContext);
  const { addToast } = useContext(ToastContext);

  const companyId = selectedCompanyId;

  const userId: number | undefined = user?.id;
  const isReadOnly: boolean = isUserReadOnly || isCompanyReadOnly;

  const messagesContainer = useChatScroll(conversation);

  const [isScheduledMessageLoading, setScheduledMessageLoading] =
    useState<boolean>(false);
  const [isUploading, setUploading] = useState<boolean>(false);
  const [isAttaching, setAttaching] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | undefined>(
    undefined,
  );
  const [messageTag, setMessageTag] = useState<MessageTagType>('general');
  const [showScheduledMessage, setScheduledMessage] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [isEditMessageOpen, setIsEditMessageOpen] = useState<boolean>(false);
  const [isEditScheduleTimeOpen, setIsEditScheduleTimeOpen] =
    useState<boolean>(false);

  const onCancelScheduleMessage = () => setScheduledMessage(false);

  const onSendReply = async () => {
    await sendMessage(value, selectedMessage?.id, '', messageTag);

    setValue('');
    setMessageTag('general');
  };

  const onSelectReply = (message: Message) => {
    setThreadSeen(conversation?.id!, message.id);
    setSelectedMessage(message);
  };

  const onCloseThread = () => setSelectedMessage(undefined);

  const topLevelMessages: Array<Message> = useMemo(() => {
    const trimmedMessagesArray: Array<Message> =
      conversation?.messages?.filter?.(
        (m: Message) => !m.message_thread_id && !m.scheduled,
      ) || [];

    const totalMessages: number = trimmedMessagesArray.length;
    const startIdx: number = Math.max(
      totalMessages - (page + 1) * CONVERSATION_MESSAGE_LIMIT,
      0,
    );
    const endIdx: number = totalMessages - page * CONVERSATION_MESSAGE_LIMIT;

    return trimmedMessagesArray.slice(startIdx, endIdx);
  }, [conversation?.messages, page]);

  const isInactiveConversation: boolean =
    conversation?.relevant_company_ids?.length === 0;

  const threadLevelMessages: Array<Message> = useMemo(() => {
    return (
      conversation?.messages?.filter?.(
        (m: Message) => m.message_thread_id === selectedMessage?.id,
      ) || []
    );
  }, [conversation?.messages, selectedMessage?.id]);

  const onOpenAttaching = () => setAttaching(true);
  const onCloseAttaching = () => setAttaching(false);

  const onDrop = useCallback(
    async <T extends File>(acceptedFiles: T[]) => {
      setUploading(true);
      let newValue: string = DOCUMENT_ANSWER_PREFIX;

      for (const file of acceptedFiles) {
        const { isValid } = await validateFile(file);

        if (!isValid) {
          addToast(
            'error',
            'We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.',
          );
          continue;
        }

        if (!companyId) {
          addToast(
            'error',
            'We were unable to upload the file, please try refreshing the page',
          );
          return;
        }

        // TODO: Fix this. Not working properly
        // if (isProtectedPDF) {
        //   addToast(
        //     'error',
        //     'The PDFs you uploaded are either password-protected or cannot be opened. Please either remove the password or upload alternative files.',
        //   );
        //   continue;
        // }

        const uploaded: any = await uploadToStorage({
          file,
          companyId: companyId!,
          path: '/Attachments',
          token,
        });

        if (uploaded?.error) {
          addToast('error', uploaded.error);
          continue;
        }

        if (newValue) {
          newValue += `${QUESTION_SEPARATOR}`;
        }

        newValue += `${uploaded?.document_location}`;
      }

      // only send message if there are attachments
      if (newValue !== DOCUMENT_ANSWER_PREFIX) {
        await sendMessage(newValue, selectedMessage?.id);
      }

      setUploading(false);
      setAttaching(false);
    },
    [token, companyId, selectedMessage?.id, sendMessage, addToast],
  );

  const hasMessages: boolean = useMemo(
    () => !isMessagesLoading && !!topLevelMessages?.length,
    [isMessagesLoading, topLevelMessages?.length],
  );

  const hasOOOAdvisor: boolean = useMemo(() => {
    return (
      !isMessagesLoading &&
      !!conversation &&
      !!conversation.advisor_out_of_office
    );
  }, [isMessagesLoading, conversation]);

  const showOOOMessage: boolean = !isInactiveConversation && hasOOOAdvisor;

  const sanitizedValue = useMemo(() => removeHtml(value), [value]);

  const onConfirmSchedule = async (date: string, time: string) => {
    try {
      setScheduledMessageLoading(true);
      const formatted = getDateFromInputs(date, time);

      await sendMessage(value, selectedMessage?.id, formatted);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setScheduledMessage(false);
      setValue('');
      setScheduledMessageLoading(false);
    }
  };

  const messageFooterClasses: string = getClasses(styles.chatContent__footer, {
    [styles.chatContent__footer__thread]: !!selectedMessage?.id,
  });

  const hasPreviousMessages: boolean =
    topLevelMessages?.length >= (page + 1) * CONVERSATION_MESSAGE_LIMIT;
  const hasNextMessages: boolean = page !== 0;

  const onNextPage = () => setPage(page - 1);
  const onPreviousPage = () => setPage(page + 1);

  const handleEditMessage = (message: Message) => {
    setEditingMessage(message);
    setIsEditMessageOpen(true);
  };

  const handleEditScheduleTime = (message: Message) => {
    setEditingMessage(message);
    setIsEditScheduleTimeOpen(true);
  };

  useEffect(() => {
    messagesContainer?.current?.scrollIntoView({
      behavior: 'auto',
      block: 'end',
    });

    reloadMessages(true);
  }, [messagesContainer, reloadMessages]);

  const chatContainerClasses: string = getClasses(
    styles.chatContent__container,
    styles.chatContent__containerAdvisor,
  );

  const chatContainerPlaceholderClasses: string = getClasses(
    styles.chatContent__containerPlaceholder,
    styles.chatContent__containerPlaceholderAdvisor,
  );

  return (
    <>
      <MessageDialog
        message={editingMessage}
        isEditMessageOpen={isEditMessageOpen}
        isEditScheduleTimeOpen={isEditScheduleTimeOpen}
        onCloseEditMessage={() => setIsEditMessageOpen(false)}
        onCloseEditScheduleTime={() => setIsEditScheduleTimeOpen(false)}
      />
      <ScheduledMessageDialog
        isOpen={showScheduledMessage}
        isLoading={isScheduledMessageLoading}
        onClose={onCancelScheduleMessage}
        onConfirm={onConfirmSchedule}
      />
      <Dialog
        isOpen={isAttaching}
        onClose={onCloseAttaching}
        title="Upload Documents"
      >
        <Dialog.Content>
          <UploadBox
            onDrop={onDrop}
            isLoading={isUploading}
            allowMultiple={true}
          />
        </Dialog.Content>
      </Dialog>
      {selectedMessage && (
        <ClientChatThreadAside onClose={onCloseThread}>
          <Flex isColumn={true} gap={6}>
            <ClientChatMessage
              isActive={true}
              isReply={true}
              onSelectReply={onSelectReply}
              isMe={selectedMessage?.user_id === userId}
              message={selectedMessage}
            />
            {threadLevelMessages?.map((message: Message, key) => {
              return (
                <ClientChatMessage
                  onSelectReply={onSelectReply}
                  isActive={false}
                  isReply={true}
                  isMe={message?.user_id === userId}
                  message={message}
                  onEditMessage={handleEditMessage}
                  onEditScheduleTime={handleEditScheduleTime}
                  key={key}
                />
              );
            })}
          </Flex>
        </ClientChatThreadAside>
      )}
      <Flex isColumn={true} className={styles.chatContent}>
        <div className={chatContainerClasses} ref={messagesContainer}>
          {hasPreviousMessages && (
            <Flex padding={8} isCentered={true}>
              <ButtonGroup>
                <Button
                  value={`Previous ${CONVERSATION_MESSAGE_LIMIT} messages`}
                  iconLeft="arrow-up"
                  intent="secondary"
                  size="small"
                  onClick={onPreviousPage}
                />
              </ButtonGroup>
            </Flex>
          )}
          {topLevelMessages?.map((message: Message, key: number) => {
            const unreadThreadMessageCount: number =
              (
                conversation?.messages?.filter?.(
                  (m: Message) => m.message_thread_id === message?.id,
                ) || []
              )?.reduce(
                (count, message) =>
                  message.unseen_user_ids.includes(user?.id!)
                    ? count + 1
                    : count,
                0,
              ) ?? 0;

            return (
              <ClientChatMessage
                isMe={message?.user_id === userId}
                isReply={false}
                unreadThreadMessageCount={unreadThreadMessageCount}
                isActive={false}
                onSelectReply={onSelectReply}
                onEditMessage={handleEditMessage}
                onEditScheduleTime={handleEditScheduleTime}
                message={message}
                key={key}
              />
            );
          })}
          {!hasMessages && <div className={chatContainerPlaceholderClasses} />}
          {isInactiveConversation && (
            <Alert type="warning">
              This conversation is archived because a new conversation has been
              created with different users. Messaging is disabled, please use
              the other conversation.
            </Alert>
          )}
          {showOOOMessage && (
            <Alert type="info">
              <InlineUnsafe
                text={conversation?.advisor_out_of_office_message}
              />
            </Alert>
          )}
          {hasNextMessages && (
            <Flex padding={8} isCentered={true}>
              <ButtonGroup>
                <Button
                  value={`Latest ${CONVERSATION_MESSAGE_LIMIT} messages`}
                  iconLeft="arrow-down"
                  intent="secondary"
                  size="small"
                  onClick={onNextPage}
                />
              </ButtonGroup>
            </Flex>
          )}
        </div>
        <UnreadThreadIndicator conversation={conversation} />
        <footer className={messageFooterClasses}>
          <TrixEditor
            value={value}
            isResponsive={true}
            setValue={setValue}
            isDisabled={
              isReadOnly || isInactiveConversation || isMessagesLoading
            }
          />
          <div className={styles.chatContent__footer__buttonGroup}>
            <ButtonGroup>
              <Button
                intent="secondary"
                isIconOnly={true}
                iconLeft="upload"
                size="tiny"
                isLoading={isUploading}
                isDisabled={isReadOnly}
                value="Upload"
                withTooltip={true}
                onClick={onOpenAttaching}
              />
              <Button
                intent="primary"
                iconLeft="send"
                size="small"
                isDisabled={isSending || isReadOnly || !sanitizedValue}
                isLoading={isSending}
                value="Send"
                onClick={onSendReply}
              />
            </ButtonGroup>
          </div>
        </footer>
      </Flex>
    </>
  );
};
